import React, { useMemo, useState, useDeferredValue } from 'react';
import { Link } from 'react-router-dom';
import {
  Icon,
  CATEGORIES,
  AREAS,
  ICON_SIZES,
  LIBRARY_VERSION,
  metadata,
} from '../icons';

const SOURCES = [
  { id: 'all', label: 'All sources' },
  { id: 'brand', label: 'Brand' },
  { id: 'animated', label: 'Animated' },
  { id: 'lucide', label: 'Lucide' },
];

const COLORWAYS = [
  { id: 'both', label: 'Dark + white' },
  { id: 'dark', label: 'Dark only' },
  { id: 'white', label: 'White only' },
];

export default function IconLibrary() {
  const [query, setQuery] = useState('');
  const [source, setSource] = useState('all');
  const [category, setCategory] = useState('all');
  const [area, setArea] = useState('all');
  const [colorway, setColorway] = useState('both');
  const [selected, setSelected] = useState(null);
  const deferredQuery = useDeferredValue(query.trim().toLowerCase());

  const icons = metadata.icons || [];
  const assets = metadata.staticAssets || [];

  const filtered = useMemo(() => {
    return icons.filter((icon) => {
      if (source !== 'all' && icon.source !== source) return false;
      if (category !== 'all' && icon.category !== category) return false;
      if (area !== 'all' && !(icon.areas || []).includes(area)) return false;
      if (!deferredQuery) return true;
      const hay = [
        icon.id,
        icon.label,
        icon.description,
        ...(icon.keywords || []),
        ...(icon.useCases || []),
        ...(icon.tags || []),
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(deferredQuery);
    });
  }, [icons, source, category, area, deferredQuery]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const cat of CATEGORIES) map.set(cat.id, []);
    for (const icon of filtered) {
      if (!map.has(icon.category)) map.set(icon.category, []);
      map.get(icon.category).push(icon);
    }
    return [...map.entries()].filter(([, list]) => list.length > 0);
  }, [filtered]);

  const active = selected ? icons.find((i) => i.id === selected) : null;
  const showDark = colorway === 'both' || colorway === 'dark';
  const showWhite = colorway === 'both' || colorway === 'white';

  return (
    <div className="min-h-screen bg-[var(--pf-canvas,#f6f7f5)] text-[var(--pf-ink,#0c0f14)]">
      <header className="border-b border-black/10 bg-[var(--pf-surface,#fff)]">
        <div className="max-w-7xl mx-auto px-4 py-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-black/50 mb-2">PetsFostering design system</p>
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Icon library</h1>
            <p className="mt-2 text-sm text-black/60 max-w-xl">
              Brand marks, animated loaders, and UI icons in dark + white — v{LIBRARY_VERSION}.{' '}
              {metadata.counts?.total || icons.length} icons · {assets.length} static assets.
            </p>
          </div>
          <Link to="/" className="text-sm underline underline-offset-4 text-black/70 hover:text-black">
            Back to app
          </Link>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 grid lg:grid-cols-[240px_1fr_300px] gap-6">
        <aside className="space-y-5">
          <label className="block">
            <span className="text-xs font-medium uppercase tracking-wide text-black/50">Search</span>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="paw, loader, booking…"
              className="mt-1.5 w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[var(--pf-accent,#3fa7a3)]"
            />
          </label>

          <FilterGroup label="Colorway" value={colorway} onChange={setColorway} options={COLORWAYS} />
          <FilterGroup label="Source" value={source} onChange={setSource} options={SOURCES} />
          <FilterGroup
            label="Category"
            value={category}
            onChange={setCategory}
            options={[{ id: 'all', label: 'All categories' }, ...CATEGORIES.map((c) => ({ id: c.id, label: c.label }))]}
          />
          <FilterGroup
            label="Product area"
            value={area}
            onChange={setArea}
            options={[{ id: 'all', label: 'All areas' }, ...AREAS.map((a) => ({ id: a.id, label: a.label }))]}
          />

          <div className="rounded-xl border border-black/10 bg-white p-3">
            <p className="text-xs font-medium uppercase tracking-wide text-black/50 mb-2">Static brand assets</p>
            <ul className="space-y-2 max-h-80 overflow-y-auto">
              {assets.map((asset) => {
                const isWhite = asset.colorway === 'white';
                return (
                  <li key={asset.id} className="flex items-center gap-2 text-xs">
                    <div
                      className={`w-8 h-8 rounded flex items-center justify-center shrink-0 ${
                        isWhite ? 'bg-[#0F1720]' : 'bg-black/[0.04]'
                      }`}
                    >
                      <img src={asset.path} alt="" className="w-6 h-6 object-contain" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium truncate">{asset.label}</div>
                      <div className="text-black/45 truncate">{asset.path}</div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </aside>

        <main className="min-w-0 space-y-8">
          <div className="flex flex-wrap gap-3 items-center text-sm text-black/55">
            <span>{filtered.length} shown</span>
            <span className="opacity-40">·</span>
            <code className="text-xs bg-black/5 px-2 py-1 rounded">
              {'<Icon name="…" className="text-white" />'}
            </code>
          </div>

          {grouped.length === 0 ? (
            <p className="text-black/50 py-16 text-center">No icons match these filters.</p>
          ) : (
            grouped.map(([catId, list]) => {
              const cat = CATEGORIES.find((c) => c.id === catId);
              return (
                <section key={catId}>
                  <div className="mb-3 flex items-baseline gap-3">
                    <h2 className="text-lg font-semibold">{cat?.label || catId}</h2>
                    <span className="text-xs text-black/40">{list.length}</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-2">
                    {list.map((icon) => (
                      <button
                        key={icon.id}
                        type="button"
                        onClick={() => setSelected(icon.id)}
                        className={`rounded-xl border p-3 text-left transition hover:border-[var(--pf-accent,#3fa7a3)] ${
                          selected === icon.id
                            ? 'border-[var(--pf-accent,#3fa7a3)] bg-white shadow-sm'
                            : 'border-black/10 bg-white/60'
                        }`}
                      >
                        <DualSwatch
                          name={icon.id}
                          size={icon.source === 'animated' ? 'xl' : 'lg'}
                          title={icon.label}
                          showDark={showDark}
                          showWhite={showWhite}
                        />
                        <div className="mt-2 text-[11px] font-medium truncate">{icon.id}</div>
                        <div className="text-[10px] text-black/40 truncate">{icon.source}</div>
                      </button>
                    ))}
                  </div>
                </section>
              );
            })
          )}
        </main>

        <aside className="lg:sticky lg:top-4 h-fit rounded-2xl border border-black/10 bg-white p-4">
          {active ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-2">
                <PreviewPane name={active.id} ink="dark" source={active.source} label={active.label} />
                <PreviewPane name={active.id} ink="white" source={active.source} label={active.label} />
              </div>
              <div>
                <h3 className="text-lg font-semibold">{active.label}</h3>
                <p className="text-xs text-black/45 mt-0.5">{active.id}</p>
              </div>
              <p className="text-sm text-black/70 leading-relaxed">{active.description}</p>
              <MetaRow label="Source" value={active.source} />
              <MetaRow label="Style" value={active.style} />
              <MetaRow label="Category" value={active.category} />
              <MetaRow label="Default size" value={active.defaultSize} />
              <MetaRow label="A11y" value={active.a11y} />
              {active.motion ? <MetaRow label="Motion" value={active.motion} /> : null}
              {active.reducedMotion ? <MetaRow label="Reduced motion" value={active.reducedMotion} /> : null}
              <div>
                <p className="text-[11px] uppercase tracking-wide text-black/45 mb-1">Areas</p>
                <div className="flex flex-wrap gap-1">
                  {(active.areas || []).map((a) => (
                    <span key={a} className="text-[11px] px-2 py-0.5 rounded-full bg-black/5">
                      {a}
                    </span>
                  ))}
                </div>
              </div>
              {(active.useCases || []).length > 0 && (
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-black/45 mb-1">Use cases</p>
                  <ul className="text-sm text-black/70 space-y-1 list-disc pl-4">
                    {active.useCases.map((u) => (
                      <li key={u}>{u}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div>
                <p className="text-[11px] uppercase tracking-wide text-black/45 mb-1">Copy — dark</p>
                <pre className="text-[11px] bg-black/[0.04] rounded-lg p-2 overflow-x-auto">{`<Icon name="${active.id}" className="text-[#0F1720]" />`}</pre>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wide text-black/45 mb-1">Copy — white</p>
                <pre className="text-[11px] bg-[#0F1720] text-white rounded-lg p-2 overflow-x-auto">{`<Icon name="${active.id}" className="text-white" />`}</pre>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wide text-black/45 mb-1">Sizes (dark)</p>
                <div className="flex flex-wrap items-end gap-3 text-[#0F1720]">
                  {Object.keys(ICON_SIZES).map((s) => (
                    <div key={s} className="text-center">
                      <Icon name={active.id} size={s} />
                      <div className="text-[10px] text-black/40 mt-1">{s}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="py-10 text-center text-sm text-black/45">
              Select an icon to inspect both colorways, sizes, and copy-paste usage.
              <div className="mt-6 grid grid-cols-2 gap-2">
                <PreviewPane name="loader-running-dog" ink="dark" source="animated" label="Preview" />
                <PreviewPane name="loader-running-dog" ink="white" source="animated" label="Preview" />
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

function DualSwatch({ name, size, title, showDark, showWhite }) {
  if (showDark && showWhite) {
    return (
      <div className="grid grid-cols-2 gap-1.5">
        <div className="h-12 rounded-lg bg-[#f6f7f5] flex items-center justify-center text-[#0F1720]">
          <Icon name={name} size={size} title={`${title} dark`} />
        </div>
        <div className="h-12 rounded-lg bg-[#0F1720] flex items-center justify-center text-white">
          <Icon name={name} size={size} title={`${title} white`} />
        </div>
      </div>
    );
  }
  if (showWhite) {
    return (
      <div className="h-12 rounded-lg bg-[#0F1720] flex items-center justify-center text-white">
        <Icon name={name} size={size} title={title} />
      </div>
    );
  }
  return (
    <div className="h-12 rounded-lg bg-[#f6f7f5] flex items-center justify-center text-[#0F1720]">
      <Icon name={name} size={size} title={title} />
    </div>
  );
}

function PreviewPane({ name, ink, source, label }) {
  const isWhite = ink === 'white';
  return (
    <div
      className={`h-28 flex flex-col items-center justify-center rounded-xl gap-1 ${
        isWhite ? 'bg-[#0F1720] text-white' : 'bg-[#f6f7f5] text-[#0F1720]'
      }`}
    >
      <Icon name={name} size={source === 'animated' ? '3xl' : '2xl'} title={label} />
      <span className={`text-[10px] ${isWhite ? 'text-white/50' : 'text-black/40'}`}>
        {isWhite ? 'White' : 'Dark'}
      </span>
    </div>
  );
}

function FilterGroup({ label, value, onChange, options }) {
  return (
    <label className="block">
      <span className="text-xs font-medium uppercase tracking-wide text-black/50">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[var(--pf-accent,#3fa7a3)]"
      >
        {options.map((opt) => (
          <option key={opt.id} value={opt.id}>
            {opt.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function MetaRow({ label, value }) {
  return (
    <div className="flex justify-between gap-3 text-sm border-b border-black/5 pb-2">
      <span className="text-black/45">{label}</span>
      <span className="font-medium text-right">{value}</span>
    </div>
  );
}
